import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import { Box, CircularProgress, IconButton, type SxProps, Tooltip } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, type MouseEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { INK } from "../../theme/tokens";
import { shouldBypassCallOffDialog, useMobile } from "../../utils/utilFunctions";
import { useDepartmentReuqestAgentIds } from "../RequestData/hooks/useAgentHistory";
import SystemDialog from "../UIToolkit/Popups/Dialog";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { useInitiateRoboCall } from "./useInitiateRoboCall";

type variant = "icon" | "text" | "both";

type Props = {
	variant?: variant;
	disabled?: boolean;
	/**
	 * Override styles for the icon.
	 */
	iconSxOverride?: SxProps;
	/**
	 * If passed, iconSxOverride is ignored and the size of the icon is set directly to this value.
	 */
	iconSizeOverride?: "small" | "medium" | "large";
	iconVariant?: "filled" | "outlined";
	requestId: string;
	date: DateTime;
};

const iconColorSx = { color: INK.SECONDARY } as const satisfies SxProps;
const iconSx = { fontSize: "16px" } as const satisfies SxProps;
const buttonSx = { fontSize: "12px", py: 0, px: 1, height: "fit-content" } as const satisfies SxProps;

/**
 * Component for initiating a robocall.
 * @param variant
 * @param disabled
 * @param iconSxOverride
 * @param iconVariant
 * @param iconSizeOverride
 * @param requestId
 */
export const InitiateRoboCallButton: FC<Props> = ({
	variant = "icon",
	disabled,
	iconSxOverride,
	iconVariant = "outlined",
	iconSizeOverride,
	requestId,
	date,
}) => {
	const { t } = useTranslation();
	const { chosenDepartment, timezone } = useHospitalData();
	const roboCallEnabled = useFeatureFlag(FEATURE_FLAGS.CallOffAgent);
	const { mutate: initiateRoboCall, isPending } = useInitiateRoboCall();
	const { data: departmentActionsMetadata } = useDepartmentReuqestAgentIds({ requestId });
	const { isMobile } = useMobile();
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);

	const handleClick = (event?: MouseEvent<HTMLElement>) => {
		event?.stopPropagation();
		if (isMobile) {
			setIsConfirmOpen(true);
			return;
		}
		initiateRoboCall({ requestId });
	};

	const handleConfirm = () => {
		setIsConfirmOpen(false);
		initiateRoboCall({ requestId });
	};

	const hasActions = useMemo(() => {
		return departmentActionsMetadata?.includes(requestId);
	}, [departmentActionsMetadata, requestId]);

	const shouldBypass = shouldBypassCallOffDialog(date, chosenDepartment, timezone);

	const mergedIconSx = {
		...iconColorSx,
		...(!iconSizeOverride
			? {
					...iconSx,
					...iconSxOverride,
				}
			: {}),
	} as const satisfies SxProps;

	const renderIcon = () => {
		if (isPending) {
			return <CircularProgress size={16} sx={mergedIconSx} />;
		}
		if (iconVariant === "outlined") {
			return <HeadsetMicOutlinedIcon fontSize={iconSizeOverride} sx={mergedIconSx} />;
		}
		return <HeadsetMicIcon fontSize={iconSizeOverride} sx={mergedIconSx} />;
	};

	if (!(roboCallEnabled && shouldBypass && !hasActions)) return null;

	return (
		<Box component="span" onClick={(e) => e.stopPropagation()} sx={{ display: "inline-flex" }}>
			{variant === "icon" ? (
				<Tooltip title={t("robocall.initiate.tooltip")}>
					<IconButton
						key={`robo-call-button-${requestId}`}
						data-testid={`robo-call-button-${requestId}`}
						size={"small"}
						disabled={disabled || isPending}
						onClick={handleClick}
					>
						{renderIcon()}
					</IconButton>
				</Tooltip>
			) : (
				<SystemButton
					key={`robo-call-button-${requestId}`}
					data-testid={`robo-call-button-${requestId}`}
					tooltip={t("robocall.initiate.tooltip")}
					buttonVariant={"secondary"}
					sx={buttonSx}
					disabled={disabled}
					loading={isPending}
					leadingIcon={
						variant === "both" ? (
							iconVariant === "outlined" ? (
								<HeadsetMicOutlinedIcon fontSize={iconSizeOverride} sx={mergedIconSx} />
							) : iconVariant === "filled" ? (
								<HeadsetMicIcon fontSize={iconSizeOverride} sx={mergedIconSx} />
							) : undefined
						) : undefined
					}
					onClick={handleClick}
				>
					{t("robocall.button.text")}
				</SystemButton>
			)}
			{isMobile && (
				<SystemDialog
					dataTestId={`robo-call-confirm-${requestId}`}
					open={isConfirmOpen}
					onClose={() => setIsConfirmOpen(false)}
					title={t("robocall.initiate.confirm.title")}
					body={t("robocall.initiate.confirm.body")}
					confirm={t("robocall.initiate.confirm.confirm")}
					onConfirm={handleConfirm}
				/>
			)}
		</Box>
	);
};
